"use client";

import { useState, FormEvent } from "react";
import Link from "next/link";
import { API_BASE } from "../../lib/api";

// Public landing + application form for the Maren creator affiliate
// program. Posts to the unauthenticated POST /affiliate/apply endpoint
// (backend rate-limits per IP). Marketing surface: follows
// DESIGN-LANGUAGE.md. No em dashes, no exclamation points, no emoji.

type Status = "idle" | "submitting" | "received" | "error";

const PLATFORMS = ["Instagram", "YouTube", "TikTok", "Reddit", "Newsletter", "Other"];
const AUDIENCE_SIZES = ["Under 1k", "1k-10k", "10k-50k", "50k-250k", "250k+"];
const AUDIENCE_TYPES = ["Premed", "MCAT", "Med student", "Mixed / other"];

const HOW_IT_WORKS = [
  {
    title: "Apply",
    body: "Tell us where you publish and who follows you. Approval is by hand, so the program stays aligned with the brand.",
  },
  {
    title: "Get your code",
    body: "You receive a personal code and a tracking link. Your audience gets 10% off their first purchase when they use it.",
  },
  {
    title: "Share Maren",
    body: "Recommend Maren where it fits your content. We give you accurate copy and assets to work from.",
  },
  {
    title: "Earn on referrals",
    body: "You earn 20% of what each student pays on their first purchase, the full amount of a study pass or their first month of monthly access. Paid monthly.",
  },
];

const TERMS = [
  { label: "Customer discount", value: "10% off one purchase" },
  { label: "Your commission", value: "20% of net revenue" },
  { label: "Applies to", value: "First payment, any plan" },
  { label: "Payout", value: "Monthly, $50 minimum, PayPal or Wise" },
];

export default function CreatorsPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    platform: "",
    handles: "",
    audience_size: "",
    audience_type: "",
    pitch: "",
  });
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  function update(field: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
    if (status === "error") setStatus("idle");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setStatus("error");
      setErrorMessage("Add your name and email so we can reach you.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch(`${API_BASE}/affiliate/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res
          .json()
          .catch(() => ({ detail: "Could not submit right now." }));
        setStatus("error");
        setErrorMessage(
          typeof data.detail === "string" ? data.detail : "Could not submit right now.",
        );
        return;
      }
      setStatus("received");
    } catch {
      setStatus("error");
      setErrorMessage("Network error. Try again in a moment.");
    }
  }

  const inputClass =
    "w-full rounded-lg border border-maren-sage bg-white dark:bg-maren-charcoal text-maren-charcoal dark:text-maren-stone placeholder:text-maren-charcoal/40 dark:placeholder:text-maren-stone/40 px-4 py-2.5 text-sm focus:border-maren-green focus:ring-2 focus:ring-maren-gold/30 focus:outline-none";
  const labelClass =
    "block text-sm font-semibold text-maren-charcoal dark:text-maren-stone mb-1.5";

  return (
    <main className="bg-maren-stone dark:bg-maren-night text-maren-charcoal dark:text-maren-stone">
      {/* Hero */}
      <section className="bg-maren-charcoal">
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-maren-gold">
            Creator program
          </p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-medium leading-tight text-maren-stone">
            Earn with <span className="text-maren-gold">Maren</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-maren-stone/75 leading-relaxed">
            If you create for premed, MCAT, or med-student audiences, partner with Maren. Your
            audience gets 10% off their first purchase, and you earn a 20% commission on it,
            whether they buy a study pass or their first month of monthly access.
          </p>
          <div className="mt-8">
            <a
              href="#apply"
              className="inline-block rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-6 py-3 text-sm font-semibold text-maren-stone transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
            >
              Apply to the program
            </a>
          </div>
          <p className="mt-4 text-xs text-maren-stone/55">
            No cost to join. Commissions paid monthly once your balance reaches $50.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-4xl font-medium text-maren-charcoal dark:text-maren-stone">
            How it works
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.title}
                className="rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-6"
              >
                <div className="font-display text-3xl text-maren-green dark:text-maren-gold">
                  {i + 1}
                </div>
                <h3 className="mt-3 text-lg font-semibold text-maren-charcoal dark:text-maren-stone">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms summary */}
      <section className="py-20 sm:py-24 bg-maren-sage/30 dark:bg-maren-charcoal/40">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-4xl font-medium text-maren-charcoal dark:text-maren-stone">
            The offer
          </h2>
          <p className="mt-3 max-w-2xl text-base text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
            A clean 20% of what your referral first pays. A study pass counts in full, so one
            pass referral is worth more than a single subscription month.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TERMS.map((t) => (
              <div
                key={t.label}
                className="rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-6"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-maren-green dark:text-maren-gold">
                  {t.label}
                </p>
                <p className="mt-2 font-display text-2xl text-maren-charcoal dark:text-maren-stone leading-snug">
                  {t.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application */}
      <section id="apply" className="py-20 sm:py-24">
        <div className="max-w-xl mx-auto px-6">
          <h2 className="font-display text-4xl font-medium text-maren-charcoal dark:text-maren-stone">
            Apply
          </h2>
          <p className="mt-3 text-base text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
            Two minutes. We review every application by hand and reply by email.
          </p>

          {status === "received" ? (
            <div className="mt-8 rounded-2xl border border-maren-sage/50 dark:border-maren-sage/25 bg-white dark:bg-maren-charcoal p-6">
              <p className="font-display text-2xl text-maren-green dark:text-maren-gold">
                Application received.
              </p>
              <p className="mt-2 text-sm text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
                Thank you. We review applications by hand and will reach out by email. In the
                meantime, you can read more about Maren on the{" "}
                <Link
                  href="/"
                  className="underline underline-offset-2 hover:text-maren-green dark:hover:text-maren-gold"
                >
                  home page
                </Link>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-6">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Name <span className="text-maren-gold">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className={inputClass}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className={labelClass}>
                  Email <span className="text-maren-gold">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="platform" className={labelClass}>
                    Main platform
                  </label>
                  <select
                    id="platform"
                    value={form.platform}
                    onChange={(e) => update("platform", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="audience_size" className={labelClass}>
                    Audience size
                  </label>
                  <select
                    id="audience_size"
                    value={form.audience_size}
                    onChange={(e) => update("audience_size", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {AUDIENCE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="audience_type" className={labelClass}>
                  Audience
                </label>
                <select
                  id="audience_type"
                  value={form.audience_type}
                  onChange={(e) => update("audience_type", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select</option>
                  {AUDIENCE_TYPES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="handles" className={labelClass}>
                  Links or handles
                </label>
                <input
                  id="handles"
                  type="text"
                  value={form.handles}
                  onChange={(e) => update("handles", e.target.value)}
                  className={inputClass}
                  placeholder="Links to your channel, profile, or newsletter"
                />
              </div>

              <div>
                <label htmlFor="pitch" className={labelClass}>
                  How would you share Maren
                </label>
                <textarea
                  id="pitch"
                  rows={3}
                  value={form.pitch}
                  onChange={(e) => update("pitch", e.target.value)}
                  className={inputClass}
                  placeholder="One or two sentences"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-6 py-3 text-sm font-semibold text-maren-stone transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-maren-gold/50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "Submitting..." : "Submit application"}
                </button>
                {status === "error" && errorMessage && (
                  <p className="mt-3 text-xs text-terracotta">{errorMessage}</p>
                )}
              </div>

              <p className="text-xs text-maren-charcoal/55 dark:text-maren-stone/55 leading-relaxed">
                Approved creators agree to the program terms, including clear disclosure of the
                affiliate relationship and no score or admissions guarantees. Maren is an
                independent study tool, not affiliated with the AAMC.
              </p>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
